/**
 * QR コードの遷移先 = 自治体の防災ポータルを住所から引く。
 *
 * 以前は全物件が東京都防災ホームページ固定だった。避難先は区市町村が指定するので、
 * 住民が次に見るべきページも都ではなく**その自治体**の防災ポータル。
 *
 * 収録方針:
 *  - 自治体の**自前ドメイン**に専用ポータルがあればそれ (例 中央区 bosai.city.chuo.lg.jp)。
 *  - 専用ポータルが外部ホストの場合は区の公式サイトの防災ページを採る
 *    (港区 city-minato.my.site.com / 世田谷区 setagaya-bousai.my.site.com /
 *     台東区 taito.portal.dmacs.jp / 葛飾区 city-katsushika.eye-bousai-plus.com)。
 *    印刷物の QR から見知らぬドメインへ飛ばさないため。
 *  - 収録は 東京23区 + 対象市場の政令市。それ以外は全国のハザードマップポータルへ。
 *
 * URL は全件 HTTP 200 と <title> を実測して収録した (2026-08-28)。
 * リンク切れは起こりうるので、増やすときも必ず実際に引いて確かめること
 * (実測でシードにあった札幌市・広島市の URL は 404 になっていた)。
 */

/**
 * 該当自治体が無いときの遷移先 (国土地理院 ハザードマップポータルサイト)。
 * 全国どの住所でも自分の地域の危険度と避難先を辿れるため、都の固定より安全。
 */
export const NATIONAL_BOUSAI_PORTAL = 'https://disaportal.gsi.go.jp/';

/**
 * 「都道府県 + 市区町村」→ 防災ポータル。
 * キーを都道府県から書くのは、同名の自治体を取り違えないため (北区・中央区など)。
 */
export const BOUSAI_PORTALS: Readonly<Record<string, string>> = {
  // --- 東京23区 ---
  東京都千代田区: 'https://bosai.city.chiyoda.lg.jp/',
  東京都中央区: 'https://bosai.city.chuo.lg.jp/',
  東京都港区: 'https://www.city.minato.tokyo.jp/bosai-anzen/bosai/',
  東京都新宿区: 'https://www.city.shinjuku.lg.jp/anzen/index03.html',
  東京都文京区: 'https://bosai.city.bunkyo.lg.jp/',
  東京都台東区: 'https://www.city.taito.lg.jp/bosai/index.html',
  東京都墨田区: 'https://www.city.sumida.lg.jp/anzen_anshin/index.html',
  東京都江東区: 'https://bosai.city.koto.lg.jp/',
  東京都品川区: 'https://bosai.city.shinagawa.tokyo.jp/',
  東京都目黒区: 'https://www.city.meguro.tokyo.jp/bousaianzen/bousai/index.html',
  東京都大田区: 'https://bosaipotal.city.ota.tokyo.jp/',
  東京都世田谷区:
    'https://www.city.setagaya.lg.jp/kurashi/kyuukyuu/category/11692.html',
  東京都渋谷区: 'https://bosai.city.shibuya.tokyo.jp/',
  東京都中野区: 'https://www.city.tokyo-nakano.lg.jp/bosai/index.html',
  東京都杉並区: 'https://www.city.suginami.tokyo.jp/bousai/index.html',
  東京都豊島区: 'https://bosai.city.toshima.lg.jp/',
  東京都北区: 'https://bosaiportal.city.kita.lg.jp/',
  東京都荒川区: 'https://bosai.city.arakawa.tokyo.jp/',
  東京都板橋区: 'https://www.city.itabashi.tokyo.jp/bousai/bousai/index.html',
  東京都練馬区: 'https://www.city.nerima.tokyo.jp/kurashi/bosai/index.html',
  東京都足立区: 'https://bosai.city.adachi.tokyo.jp/',
  東京都葛飾区:
    'https://www2.city.katsushika.lg.jp/kurashi/1004028/1000063/index.html',
  東京都江戸川区: 'https://bosai.city.edogawa.tokyo.jp/',

  // --- 対象市場の政令市 (GRAND の設置基準の対象都市) ---
  北海道札幌市: 'https://bousai.city.sapporo.jp/',
  宮城県仙台市:
    'https://www.city.sendai.jp/kikikanri/kurashi/anzen/saigaitaisaku/sonaete/joho/index.html',
  愛知県名古屋市: 'https://www.city.nagoya.jp/bousaiportal/index.html',
  大阪府大阪市: 'https://www.osaka-bousai.net/',
  広島県広島市: 'https://www.city.hiroshima.lg.jp/saigaiinfo/index.html',
  福岡県福岡市: 'https://www.city.fukuoka.lg.jp/bousai/index.html',
  神奈川県横浜市:
    'https://www.city.yokohama.lg.jp/bousai-kyukyu-bohan/bousai-saigai/index.html',

  // --- 都道府県フォールバック (収録済みの市区町村に当たらなかった場合) ---
  東京都: 'https://www.bousai.metro.tokyo.lg.jp/',
};

/**
 * 住所照合用の正規化。
 * 全角/半角・空白の揺れを吸収し、先頭の郵便番号を落とす
 * (マスタ・シードの住所は「〒103-0025 東京都中央区…」の形で入ることがあり、
 *  前方一致がそのままでは外れる)。
 */
function normalize(s: string): string {
  return String(s ?? '')
    .normalize('NFKC')
    .replace(/[\s\u3000]/g, '')
    .replace(/^〒?\d{3}-?\d{4}/, '');
}

/**
 * 前方一致で引くキー。長い順に見る —
 * 「東京都北区…」が「東京都」に先に当たって都のページになるのを防ぐ。
 */
const KEYS_BY_LENGTH = Object.keys(BOUSAI_PORTALS).sort(
  (a, b) => b.length - a.length,
);

/**
 * 都道府県が省略された住所 (「渋谷区桜丘町…」) 用の、市区町村だけのキー。
 * 表の中で一意な市区町村名だけを対象にする (同名があるものは曖昧なので引かない)。
 */
const UNIQUE_MUNICIPALITY_KEYS: ReadonlyMap<string, string> = (() => {
  const count = new Map<string, number>();
  const url = new Map<string, string>();
  for (const [key, value] of Object.entries(BOUSAI_PORTALS)) {
    const muni = key.replace(/^(北海道|東京都|京都府|大阪府|.{2,3}県)/, '');
    if (!muni) continue; // 都道府県だけのキーは対象外
    count.set(muni, (count.get(muni) ?? 0) + 1);
    url.set(muni, value);
  }
  const out = new Map<string, string>();
  for (const [muni, n] of count) {
    if (n === 1) out.set(muni, url.get(muni)!);
  }
  return out;
})();

const UNIQUE_KEYS_BY_LENGTH = [...UNIQUE_MUNICIPALITY_KEYS.keys()].sort(
  (a, b) => b.length - a.length,
);

/**
 * 住所から防災ポータルの URL を返す。該当が無ければ全国のハザードマップポータル。
 *
 * 一致は前方一致で行う。汎用の住所パーサを書かないのは、「四日市市」「野々市市」の
 * ように市区町村名の途中に「市」を含む自治体で誤爆するため — 収録済みのキーと
 * 突き合わせるだけなら誤爆しない。
 */
export function resolveBousaiPortalUrl(address: string): string {
  const a = normalize(address);
  if (!a) return NATIONAL_BOUSAI_PORTAL;
  for (const key of KEYS_BY_LENGTH) {
    if (a.startsWith(key)) return BOUSAI_PORTALS[key]!;
  }
  // 都道府県が無い住所への救済 (表の中で一意な市区町村名のみ)
  for (const key of UNIQUE_KEYS_BY_LENGTH) {
    if (a.startsWith(key)) return UNIQUE_MUNICIPALITY_KEYS.get(key)!;
  }
  return NATIONAL_BOUSAI_PORTAL;
}
